import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { Autocomplete } from 'ComponentLibrary/src/piazza/forms'
import { GridLegacy as Grid } from '@mui/material'
import {
    AssetUsageType,
    GARAGE_SUB_USAGE_TYPE_LABELS,
    RESIDENTIAL_SUB_USAGE_TYPE_LABELS,
    RETAIL_SUB_USAGE_TYPE_LABELS,
    USAGE_TYPE_LABELS,
} from 'ComponentLibrary/src/common/usage-type'
import { HTMLAttributes, useContext } from 'react'
import { isEqual } from 'lodash'
import { FormTextWithPreSelects } from 'ComponentLibrary/src/atoms/forms'
import { InfoTooltip } from 'ComponentLibrary/src/atoms/IconTooltip'

type UsageTypeMultiSelectProps = {
    defaultValues?: AssetUsageType[]
    setValue: (newValue: AssetUsageType[]) => void
    label?: string
    tooltipText?: string
    options?: UsageTypeOption[]
    unknownLabel?: string
}

export type UsageTypeOption = { label: string; value: AssetUsageType }

export const UsageTypeMultiSelect = ({
    defaultValues,
    setValue,
    label = 'Nutzungsart / Objektart',
    tooltipText = `Bitte nennen Sie die Nutzungs- bzw. Objektart aller Objekte die Teil dieser Bewertung sind.
                          Es können mehrere Optionen ausgewählt werden.
                          Sollte die passende Objektart nicht gelistet sein, können Sie auch Freitext eingeben.`,
    options = USAGE_TYPE_OPTIONS,
    unknownLabel = 'Unbekannt',
}: UsageTypeMultiSelectProps) => {
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const findUsageTypeValueByLabel = (label: string) => options.find((option) => option.label === label)?.value
    const findUsageTypeLabelByValue = (value: AssetUsageType) =>
        options.find((option) => isEqual(option.value, value))?.label ?? value.sub_usage_type ?? unknownLabel

    if (usePiazzaInputs) {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    freeSolo
                    autoSelect
                    clearOnBlur
                    includeInputInList
                    label={label}
                    endAdornment={<InfoTooltip text={tooltipText} />}
                    options={options.map((option) => ({ label: option.label, value: option.label }))}
                    defaultValue={defaultValues?.map(findUsageTypeLabelByValue) ?? []}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && event.shiftKey) {
                            event.defaultMuiPrevented = true
                        }
                    }}
                    onValueChange={(labels) =>
                        setValue(
                            (labels ?? []).map(
                                (label) =>
                                    findUsageTypeValueByLabel(label) ?? {
                                        usage_type: 'other',
                                        sub_usage_type: label,
                                    }
                            )
                        )
                    }
                    renderOption={({ key, ...props }, option) => {
                        const isGroupOption = !findUsageTypeValueByLabel(option.label)?.sub_usage_type
                        return (
                            <li
                                key={key}
                                {...props}
                                style={{ fontWeight: isGroupOption ? 600 : 400, paddingLeft: isGroupOption ? '12px' : '22px' }}
                            >
                                {option.label}
                            </li>
                        )
                    }}
                />
            </Grid>
        )
    }

    return (
        <FormTextWithPreSelects<{ [x: string]: string[] }>
            fieldName="assetUsageTypes"
            label={label}
            fullWidth
            freeSolo
            multiple
            options={options.map((option) => option.label)}
            endAdornment={<InfoTooltip size={'20px'} style={{ height: '40px', width: '40px', display: 'flex' }} text={tooltipText} />}
            renderOption={(props: HTMLAttributes<HTMLLIElement>, option: string) => {
                const isGroupOption = !findUsageTypeValueByLabel(option)?.sub_usage_type
                return (
                    <li {...props} style={{ fontWeight: isGroupOption ? 600 : 400, paddingLeft: isGroupOption ? '12px' : '22px' }}>
                        {option}
                    </li>
                )
            }}
            defaultValue={defaultValues?.map(findUsageTypeLabelByValue) || []}
            setValue={(_, newLabels) => {
                setValue(
                    newLabels.map(
                        (newLabel) =>
                            findUsageTypeValueByLabel(newLabel) ?? {
                                usage_type: 'other',
                                sub_usage_type: newLabel,
                            }
                    )
                )
            }}
        />
    )
}

export const USAGE_TYPE_OPTIONS: UsageTypeOption[] = [
    {
        value: { usage_type: 'residential' },
        label: USAGE_TYPE_LABELS.residential,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'apartment' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.apartment,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'single_family_house' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.single_family_house,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'two_family_house' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.two_family_house,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'semi_detached_house' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.semi_detached_house,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'townhouse_middle' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.townhouse_middle,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'townhouse_end' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.townhouse_end,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'apartment_building' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.apartment_building,
    },
    {
        value: { usage_type: 'residential', sub_usage_type: 'mixed_use_apartment_building' },
        label: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.mixed_use_apartment_building,
    },
    {
        value: { usage_type: 'retail' },
        label: USAGE_TYPE_LABELS.retail,
    },
    {
        value: { usage_type: 'retail', sub_usage_type: 'consumer_market' },
        label: RETAIL_SUB_USAGE_TYPE_LABELS.consumer_market,
    },
    {
        value: { usage_type: 'retail', sub_usage_type: 'department_store' },
        label: RETAIL_SUB_USAGE_TYPE_LABELS.department_store,
    },
    {
        value: { usage_type: 'retail', sub_usage_type: 'car_dealer' },
        label: RETAIL_SUB_USAGE_TYPE_LABELS.car_dealer,
    },
    {
        value: { usage_type: 'office' },
        label: USAGE_TYPE_LABELS.office,
    },
    {
        value: { usage_type: 'mixed' },
        label: USAGE_TYPE_LABELS.mixed,
    },
    {
        value: { usage_type: 'garage' },
        label: USAGE_TYPE_LABELS.garage,
    },
    {
        value: { usage_type: 'garage', sub_usage_type: 'single_car_garage' },
        label: GARAGE_SUB_USAGE_TYPE_LABELS.single_car_garage,
    },
    {
        value: { usage_type: 'garage', sub_usage_type: 'multi_car_garage' },
        label: GARAGE_SUB_USAGE_TYPE_LABELS.multi_car_garage,
    },
    {
        value: { usage_type: 'garage', sub_usage_type: 'commercial_vehicle_garage' },
        label: GARAGE_SUB_USAGE_TYPE_LABELS.commercial_vehicle_garage,
    },
    {
        value: { usage_type: 'garage', sub_usage_type: 'multi_story_parking_garage' },
        label: GARAGE_SUB_USAGE_TYPE_LABELS.multi_story_parking_garage,
    },
    {
        value: { usage_type: 'garage', sub_usage_type: 'underground_parking_garage' },
        label: GARAGE_SUB_USAGE_TYPE_LABELS.underground_parking_garage,
    },
    {
        value: { usage_type: 'unknown' },
        label: 'Unbekannt',
    },
]
